import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Contract, type Modality, type ProjectDetail } from '../api';
import { ErrorAlert, Field, fieldError, Loading } from '../components/ui';
import { hm, MODALITY_LABEL, money, parseHoursInput, parseMoneyInput, todayIso } from '../format';

const MODALITY_HELP: Record<Modality, string> = {
  avulso: 'Cobrança após a execução: cada execução confirmada gera um item faturável (horas × valor da hora).',
  banco_horas: 'Horas contratadas que são consumidas pelas execuções. Consumir horas de banco já faturado não gera nova cobrança.',
  valor_fechado: 'Preço fechado cobrado por parcelas/marcos. Execuções registram esforço, sem cobrança por hora.',
  mensalidade: 'Valor por competência. Atendimentos do mês não geram cobranças adicionais por sessão.',
};

const fromMoney = (v: string | null) => (v ? v.replace('.', ',') : '');
const fromMinutes = (v: number | null) => (v == null ? '' : v % 60 === 0 ? String(v / 60) : `${Math.floor(v / 60)}h${String(v % 60).padStart(2, '0')}`);

export function ContractFormPage() {
  const { projectId: routeProjectId, id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const existing = useQuery({ queryKey: ['contract', id], queryFn: () => api.get<Contract>(`/contracts/${id}`), enabled: editing });
  const projectId = routeProjectId ?? existing.data?.projectId;
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.get<ProjectDetail>(`/projects/${projectId}`), enabled: Boolean(projectId) });

  const [form, setForm] = useState({
    code: '',
    modality: 'banco_horas' as Modality,
    parentContractId: '',
    startDate: todayIso(),
    endDate: '',
    hourlyRate: '',
    hours: '',
    totalValue: '',
    monthlyValue: '',
    overageRule: 'pendencia' as Contract['overageRule'],
    lowBalanceHours: '',
    externalReference: '',
    status: 'ativo' as Contract['status'],
    notes: '',
  });
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    const c = existing.data;
    if (c)
      setForm({
        code: c.code,
        modality: c.modality,
        parentContractId: c.parentContractId ?? '',
        startDate: c.startDate,
        endDate: c.endDate ?? '',
        hourlyRate: fromMoney(c.hourlyRate),
        hours: fromMinutes(c.contractedMinutes),
        totalValue: fromMoney(c.totalValue),
        monthlyValue: fromMoney(c.monthlyValue),
        overageRule: c.overageRule,
        lowBalanceHours: fromMinutes(c.lowBalanceMinutes),
        externalReference: c.externalReference ?? '',
        status: c.status,
        notes: c.notes ?? '',
      });
  }, [existing.data]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Conversão das entradas em formato brasileiro ("1.500,00", "1h30") para o formato da API.
  function buildBody() {
    const money = (label: string, v: string) => {
      if (!v.trim()) return null;
      const parsed = parseMoneyInput(v);
      if (!parsed) throw new Error(`${label}: valor inválido. Use, por exemplo, 200,00.`);
      return parsed;
    };
    const minutes = (label: string, v: string) => {
      if (!v.trim()) return null;
      const parsed = parseHoursInput(v);
      if (parsed == null || parsed <= 0) throw new Error(`${label}: use horas como 20, 1,5 ou 1h30.`);
      return parsed;
    };
    return {
      code: form.code || undefined,
      projectId,
      parentContractId: form.parentContractId || null,
      modality: form.modality,
      startDate: form.startDate,
      endDate: form.endDate || null,
      hourlyRate: money('Valor da hora', form.hourlyRate),
      contractedMinutes: minutes('Horas contratadas', form.hours),
      totalValue: money('Valor total', form.totalValue),
      monthlyValue: money('Valor mensal', form.monthlyValue),
      overageRule: form.overageRule,
      lowBalanceMinutes: minutes('Aviso de saldo baixo', form.lowBalanceHours),
      externalReference: form.externalReference,
      status: form.status,
      notes: form.notes,
    };
  }

  const m = useMutation({
    mutationFn: async () => {
      const body = buildBody();
      if (editing) {
        await api.put(`/contracts/${id}`, { ...body, version: existing.data!.version });
        return id!;
      }
      return (await api.post<{ id: string }>('/contracts', body)).id;
    },
    onSuccess: (cid) => {
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['contract', cid] });
      // Banco de horas novo: o próximo passo é registrar a contratação (ou o saldo de abertura).
      navigate(!editing && form.modality === 'banco_horas' ? `/bancos-de-horas/${cid}` : `/contratos/${cid}`);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    try {
      buildBody();
    } catch (err) {
      setLocalError((err as Error).message);
      return;
    }
    m.mutate();
  }

  if ((editing && existing.isLoading) || project.isLoading) return <Loading />;
  const err = m.error;
  const mod = form.modality;
  const previewMinutes = parseHoursInput(form.hours);
  const previewRate = parseMoneyInput(form.hourlyRate);

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to={`/projetos/${projectId}`}>← {project.data?.name}</Link>
          </p>
          <h1>{editing ? `Editar contrato ${existing.data?.code}` : 'Novo contrato'}</h1>
          <p>{project.data?.clientTradeName || project.data?.clientName}</p>
        </div>
      </div>
      <form className="card form" onSubmit={submit} noValidate style={{ maxWidth: 860 }}>
        {localError && <div className="alert alert-error" role="alert">{localError}</div>}
        <ErrorAlert error={err} />
        <Field label="Modalidade" required hint={editing ? 'A modalidade não muda depois de criada; encerre e crie outro contrato.' : MODALITY_HELP[mod]}>
          {(p) => (
            <select {...p} value={mod} disabled={editing} onChange={(e) => set('modality', e.target.value as Modality)}>
              {(Object.keys(MODALITY_LABEL) as Modality[]).map((k) => (
                <option key={k} value={k}>
                  {MODALITY_LABEL[k]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className="form-row">
          <Field label="Código" hint={editing ? undefined : 'Vazio = automático (CTR-0001).'}>
            {(p) => <input {...p} value={form.code} onChange={(e) => set('code', e.target.value)} />}
          </Field>
          <Field label="Aditivo de" hint="Opcional: contrato original deste projeto.">
            {(p) => (
              <select {...p} value={form.parentContractId} onChange={(e) => set('parentContractId', e.target.value)}>
                <option value="">Não é aditivo</option>
                {project.data?.contracts
                  .filter((c) => c.id !== id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} — {MODALITY_LABEL[c.modality]}
                    </option>
                  ))}
              </select>
            )}
          </Field>
        </div>
        <div className="form-row">
          <Field label="Início da vigência" required error={fieldError(err, 'startDate')}>
            {(p) => <input {...p} type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />}
          </Field>
          <Field label="Fim da vigência">
            {(p) => <input {...p} type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />}
          </Field>
        </div>

        <div className="form-row">
          {(mod === 'avulso' || mod === 'banco_horas') && (
            <Field label="Valor da hora (R$)" required error={fieldError(err, 'hourlyRate')}>
              {(p) => <input {...p} inputMode="decimal" placeholder="200,00" value={form.hourlyRate} onChange={(e) => set('hourlyRate', e.target.value)} />}
            </Field>
          )}
          {mod !== 'avulso' && (
            <Field
              label={mod === 'mensalidade' ? 'Horas por mês (opcional)' : mod === 'valor_fechado' ? 'Horas estimadas (opcional)' : 'Horas contratadas'}
              required={mod === 'banco_horas'}
              hint="Ex.: 20, 1,5 ou 1h30 (1h30 = 1,5 hora)."
              error={fieldError(err, 'contractedMinutes')}
            >
              {(p) => <input {...p} inputMode="decimal" value={form.hours} onChange={(e) => set('hours', e.target.value)} />}
            </Field>
          )}
        </div>
        {(mod === 'banco_horas' || mod === 'valor_fechado') && (
          <Field
            label={mod === 'valor_fechado' ? 'Valor fechado (R$)' : 'Valor do banco (R$)'}
            required={mod === 'valor_fechado'}
            hint={
              mod === 'banco_horas'
                ? previewMinutes && previewRate
                  ? `Vazio = horas × valor da hora (${hm(previewMinutes)} × ${money(previewRate)}).`
                  : 'Vazio = horas × valor da hora. Preencha se o valor negociado for diferente.'
                : undefined
            }
            error={fieldError(err, 'totalValue')}
          >
            {(p) => <input {...p} inputMode="decimal" value={form.totalValue} onChange={(e) => set('totalValue', e.target.value)} />}
          </Field>
        )}
        {mod === 'mensalidade' && (
          <Field label="Valor mensal (R$)" required error={fieldError(err, 'monthlyValue')}>
            {(p) => <input {...p} inputMode="decimal" value={form.monthlyValue} onChange={(e) => set('monthlyValue', e.target.value)} />}
          </Field>
        )}
        {mod === 'banco_horas' && (
          <div className="form-row">
            <Field label="Excedente" hint="Nenhum excedente é cobrado automaticamente; isto só define a sugestão apresentada.">
              {(p) => (
                <select {...p} value={form.overageRule} onChange={(e) => set('overageRule', e.target.value as Contract['overageRule'])}>
                  <option value="pendencia">Manter como pendência até decisão</option>
                  <option value="avulso_autorizado">Sugerir cobrança avulsa (exige autorização)</option>
                </select>
              )}
            </Field>
            <Field label="Avisar quando o saldo ficar abaixo de (horas)">
              {(p) => <input {...p} inputMode="decimal" value={form.lowBalanceHours} onChange={(e) => set('lowBalanceHours', e.target.value)} />}
            </Field>
          </div>
        )}
        <div className="form-row">
          <Field label="Referência externa" hint="Número no aplicativo financeiro ou do contrato assinado.">
            {(p) => <input {...p} value={form.externalReference} onChange={(e) => set('externalReference', e.target.value)} />}
          </Field>
          <Field label="Situação">
            {(p) => (
              <select {...p} value={form.status} onChange={(e) => set('status', e.target.value as Contract['status'])}>
                <option value="ativo">Ativo</option>
                <option value="encerrado">Encerrado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            )}
          </Field>
        </div>
        <Field label="Observações">{(p) => <textarea {...p} value={form.notes} onChange={(e) => set('notes', e.target.value)} />}</Field>
        {editing && <p className="small muted">Alterar o valor da hora vale para novas cobranças. Cobranças já geradas mantêm o preço original.</p>}
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending}>
            {m.isPending ? 'Salvando…' : 'Salvar contrato'}
          </button>
          <button type="button" className="btn" onClick={() => navigate(-1)}>
            Cancelar
          </button>
        </div>
      </form>
    </>
  );
}
